import React, { useState } from "react";

const defaultColors = [
    "#002E6E", "#2E88E9", "#95C1FF", "#A319C3", "#783AA8", "#f97316", "#10b981"
];

export default function PieChart({
    data = [],
    type = "donut",         // "pie" | "donut"
    thickness = 40,       // px or % string for donut ring
    size = 204,           // px number or "100%" for responsive
    hoverOffset = "3%",   // px or % of radius
    hoverScale = 1.03     // scale factor
}) {
    const [hover, setHover] = useState(null);
    const [pos, setPos] = useState({ x: 0, y: 0 });

    // resolve size
    const isResponsive = typeof size === "string" && size.endsWith("%");
    const pixelSize = isResponsive ? 300 : Number(size); // internal viewBox
    const radius = pixelSize / 2;

    // resolve thickness
    let resolvedThickness;
    if (typeof thickness === "string" && thickness.endsWith("%")) {
        const percent = parseFloat(thickness) / 100;
        resolvedThickness = radius * percent;
    } else {
        resolvedThickness = Number(thickness);
    }
    const innerRadius = type === "donut" ? radius - resolvedThickness : 0;

    const total = Math.max(1, data.reduce((s, d) => s + d.value, 0));

    function polarToXY(percent, r) {
        const angle = (percent * 2 * Math.PI) - Math.PI / 2;
        return [radius + r * Math.cos(angle), radius + r * Math.sin(angle)];
    }

    // ---- single slice special case ----
    if (data.length === 1) {
        const s = {
            ...data[0],
            color: data[0].color || defaultColors[0]
        };

        return (
            <div style={{ position: "relative", width: size, height: size }}>
                <svg
                    viewBox={`0 0 ${pixelSize} ${pixelSize}`}
                    width={isResponsive ? "100%" : pixelSize}
                    height={isResponsive ? "100%" : pixelSize}
                    style={{ display: "block", overflow: "visible" }}
                >
                    <circle
                        cx={radius}
                        cy={radius}
                        r={radius}
                        fill={s.color}
                        onMouseEnter={(e) => {
                            setHover(s);
                            setPos({ x: e.clientX, y: e.clientY });
                        }}
                        onMouseMove={(e) => {
                            setPos({ x: e.clientX, y: e.clientY });
                        }}
                        onMouseLeave={() => setHover(null)}
                        style={{
                            cursor: "default",
                            transition: "transform 0.2s ease, filter 0.2s ease",
                            transform: hover?.name === s.name ? `scale(${hoverScale})` : "scale(1)",
                            transformOrigin: "50% 50%",
                            filter: hover?.name === s.name
                                ? "drop-shadow(0 2px 6px rgba(0,0,0,0.35))"
                                : "none"
                        }}
                    />
                    {type === "donut" && (
                        <circle
                            cx={radius}
                            cy={radius}
                            r={innerRadius}
                            fill="#fff"
                            pointerEvents="none"
                        />
                    )}
                </svg>

                {hover && (
                    <Tooltip x={pos.x} y={pos.y}>
                        {hover.name}: {hover.value}{hover.unit || ""}
                    </Tooltip>
                )}
            </div>
        );
    }
    // ---- end single slice ----

    // multi-slice case
    let cumulative = 0;
    const slices = data.map((d, i) => {
        const valuePercent = d.value / total;
        const start = cumulative;
        cumulative += valuePercent;
        const end = cumulative;

        const [x1, y1] = polarToXY(start, radius);
        const [x2, y2] = polarToXY(end, radius);
        const [ix1, iy1] = polarToXY(start, innerRadius);
        const [ix2, iy2] = polarToXY(end, innerRadius);

        const largeArc = end - start > 0.5 ? 1 : 0;
        const path = `
      M ${x1} ${y1}
      A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
      L ${ix2} ${iy2}
      A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1}
      Z
    `;

        return {
            ...d,
            color: d.color || defaultColors[i % defaultColors.length],
            start, end,
            path
        };
    });

    function resolveOffset(val, r) {
        if (typeof val === "string" && val.endsWith("%")) {
            return (parseFloat(val) / 100) * r;
        }
        return Number(val);
    }

    return (
        <div style={{ position: "relative", width: size, height: size }}>
            <svg
                viewBox={`0 0 ${pixelSize} ${pixelSize}`}
                width={isResponsive ? "100%" : pixelSize}
                height={isResponsive ? "100%" : pixelSize}
                style={{ display: "block", overflow: "visible" }}
            >
                {slices.map((s, i) => {
                    const mid = (s.start + s.end) / 2;
                    const theta = (mid * 2 * Math.PI) - Math.PI / 2;

                    const offset = resolveOffset(hoverOffset, radius);
                    const dx = hover?.name === s.name ? Math.cos(theta) * offset : 0;
                    const dy = hover?.name === s.name ? Math.sin(theta) * offset : 0;
                    const scale = hover?.name === s.name ? hoverScale : 1;

                    return (
                        <path
                            key={i}
                            d={s.path}
                            fill={s.color}
                            stroke="#fff"
                            strokeWidth={1}
                            onMouseEnter={(e) => {
                                setHover(s);
                                setPos({ x: e.clientX, y: e.clientY });
                            }}
                            onMouseMove={(e) => {
                                setPos({ x: e.clientX, y: e.clientY });
                            }}
                            onMouseLeave={() => setHover(null)}
                            style={{
                                cursor: "default",
                                transition: "transform 0.2s ease, filter 0.2s ease",
                                transform: `translate(${dx}px, ${dy}px) scale(${scale})`,
                                transformOrigin: "50% 50%",
                                filter: hover?.name === s.name
                                    ? "drop-shadow(0 2px 6px rgba(0,0,0,0.35))"
                                    : "none"
                            }}
                        />
                    );
                })}
            </svg>

            {hover && (
                <Tooltip x={pos.x} y={pos.y}>
                    {hover.name}: {hover.value}{hover.unit || ""}
                </Tooltip>
            )}
        </div>
    );
}

// Floating tooltip with auto-flip
function Tooltip({ x, y, children }) {
    const padding = 12;
    const tooltipWidth = 140;
    const tooltipHeight = 32;

    let left = x + padding;
    let top = y + padding;

    if (left + tooltipWidth > window.innerWidth) {
        left = x - tooltipWidth - padding;
    }
    if (top + tooltipHeight > window.innerHeight) {
        top = y - tooltipHeight - padding;
    }

    return (
        <div
            style={{
                position: "fixed",
                top,
                left,
                background: "#111",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: 4,
                fontSize: 12,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                zIndex: 9999,
                maxWidth: tooltipWidth
            }}
        >
            {children}
        </div>
    );
}
